/**
 * Kling 3.0 Test Page - Secret test page for Kling 3.0 via fal.ai
 * 
 * Features Elements support for character consistency across shots.
 * Access via: /kling3-test (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Zap, Loader2, Image as ImageIcon, Film,
  Upload, Trash2, Plus,
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, Video, ArrowLeft, Eye, EyeOff, Copy, Info, User
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAX_ELEMENTS = 4;
const MAX_REF_IMAGES = 5;

const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

export default function Kling3Test() {
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [prompt, setPrompt] = useState("");
  const [startImageUrl, setStartImageUrl] = useState("");
  const [endImageUrl, setEndImageUrl] = useState("");
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [generateAudio, setGenerateAudio] = useState(false);
  const [negativePrompt, setNegativePrompt] = useState("blur, distortion, morphing, low quality");
  
  // Elements state - for character consistency
  const [elements, setElements] = useState([]);
  
  // Upload refs
  const startImageRef = useRef(null);
  const endImageRef = useRef(null);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadJobHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/kling3-test/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load config:", error);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/kling3-test/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/uploads/temp", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      return URL.createObjectURL(file);
    }
  };

  const handleStartImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    setStartImageUrl(url);
    toast.success("Start image uploaded");
  };

  const handleEndImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    setEndImageUrl(url);
    toast.success("End image uploaded");
  };

  // Element management
  const addElement = () => {
    if (elements.length >= MAX_ELEMENTS) {
      toast.error(`Maximum ${MAX_ELEMENTS} elements allowed`);
      return;
    }
    setElements(prev => [...prev, {
      id: Date.now(),
      frontalImageUrl: "",
      referenceImageUrls: [],
      uploading: false
    }]);
  };

  const removeElement = (index) => {
    setElements(prev => prev.filter((_, i) => i !== index));
  };

  const handleElementFrontalUpload = async (index, file) => {
    if (!file) return;
    
    setElements(prev => prev.map((el, i) => 
      i === index ? { ...el, uploading: true } : el
    ));
    
    const url = await uploadImage(file);
    
    setElements(prev => prev.map((el, i) => 
      i === index ? { ...el, frontalImageUrl: url, uploading: false } : el
    ));
  };

  const handleElementRefUpload = async (index, files) => {
    if (!files || files.length === 0) return;
    
    const element = elements[index];
    const remaining = MAX_REF_IMAGES - element.referenceImageUrls.length;
    const filesToUpload = Array.from(files).slice(0, remaining);
    
    for (const file of filesToUpload) {
      const url = await uploadImage(file);
      setElements(prev => prev.map((el, i) => 
        i === index 
          ? { ...el, referenceImageUrls: [...el.referenceImageUrls, url] }
          : el
      ));
    }
  };

  const removeElementRef = (elementIndex, refIndex) => {
    setElements(prev => prev.map((el, i) => 
      i === elementIndex 
        ? { ...el, referenceImageUrls: el.referenceImageUrls.filter((_, ri) => ri !== refIndex) }
        : el
    ));
  };

  const insertElementSyntax = (index) => {
    const syntax = `@Element${index + 1}`;
    setPrompt(prev => prev + (prev.endsWith(" ") || prev === "" ? "" : " ") + syntax + " ");
    toast.success(`Inserted ${syntax}`);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }
    if (!startImageUrl) {
      toast.error("Please upload a start image");
      return;
    }
    
    setGenerating(true);
    setJobStatus(null);
    
    try {
      const elementsData = elements
        .filter(el => el.frontalImageUrl)
        .map(el => ({
          frontal_image_url: el.frontalImageUrl,
          reference_image_urls: el.referenceImageUrls
        }));
      
      const response = await api.post("/kling3-test/generate", {
        prompt: prompt.trim(),
        start_image_url: startImageUrl,
        end_image_url: endImageUrl || null,
        elements: elementsData,
        duration,
        aspect_ratio: aspectRatio,
        generate_audio: generateAudio,
        negative_prompt: negativePrompt || null
      });
      
      if (response.data.success) {
        toast.success("Video generation started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start generation");
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    
    const poll = async () => {
      try {
        const response = await api.get(`/kling3-test/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.success("Video generation complete!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error(response.data.error_message || "Generation failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/kling3-test/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadVideo = async (videoUrl, jobId) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kling3_${jobId.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download video");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-orange-400" />
                <h1 className="text-lg font-semibold">Kling 3.0 Test Lab</h1>
                <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">
              Powered by fal.ai
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-orange-300">Kling 3.0 with Elements</p>
                    <p className="text-zinc-400 text-xs">
                      Use Elements for character consistency. Add up to 4 elements with multiple reference images each.
                      Reference them in your prompt using @Element1, @Element2, etc.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Start/End Images */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-orange-400" />
                  Scene Images
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Image */}
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Start Image *</Label>
                    <div 
                      className="aspect-video rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-orange-500/50 transition-colors"
                      onClick={() => startImageRef.current?.click()}
                    >
                      {startImageUrl ? (
                        <img src={getAssetUrl(startImageUrl)} alt="Start" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center p-4">
                          <Upload className="w-6 h-6 mx-auto text-zinc-500 mb-1" />
                          <p className="text-xs text-zinc-500">Click to upload</p>
                        </div>
                      )}
                    </div>
                    <input ref={startImageRef} type="file" accept="image/*" className="hidden" onChange={handleStartImageUpload} />
                    {startImageUrl && (
                      <Button variant="ghost" size="sm" className="w-full text-xs text-zinc-400 hover:text-red-400" onClick={() => setStartImageUrl("")}>
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>

                  {/* End Image */}
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">End Image (Optional)</Label>
                    <div 
                      className="aspect-video rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-orange-500/50 transition-colors"
                      onClick={() => endImageRef.current?.click()}
                    >
                      {endImageUrl ? (
                        <img src={getAssetUrl(endImageUrl)} alt="End" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center p-4">
                          <Upload className="w-6 h-6 mx-auto text-zinc-500 mb-1" />
                          <p className="text-xs text-zinc-500">Optional tail image</p>
                        </div>
                      )}
                    </div>
                    <input ref={endImageRef} type="file" accept="image/*" className="hidden" onChange={handleEndImageUpload} />
                    {endImageUrl && (
                      <Button variant="ghost" size="sm" className="w-full text-xs text-zinc-400 hover:text-red-400" onClick={() => setEndImageUrl("")}>
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Elements Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4 text-orange-400" />
                    Elements (Character Consistency)
                    <Badge variant="outline" className="text-xs text-zinc-400">
                      {elements.length}/{MAX_ELEMENTS}
                    </Badge>
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addElement}
                    disabled={elements.length >= MAX_ELEMENTS}
                    className="text-xs border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Element
                  </Button>
                </div>
                <CardDescription className="text-xs">
                  Add characters/objects with multiple reference images for consistency. Use @Element1, @Element2 in prompt.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {elements.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500 text-sm">
                    No elements added. Click "Add Element" to add characters for consistency.
                  </div>
                ) : (
                  elements.map((element, index) => (
                    <div key={element.id} className="p-3 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-orange-600 text-white text-xs">
                            @Element{index + 1}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px] text-orange-400"
                            onClick={() => insertElementSyntax(index)}
                          >
                            <Copy className="w-3 h-3 mr-1" /> Insert
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                          onClick={() => removeElement(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2">
                        {/* Frontal Image */}
                        <div className="space-y-1">
                          <Label className="text-[10px] text-zinc-500">Frontal *</Label>
                          <div 
                            className="aspect-square rounded border border-dashed border-zinc-600 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-orange-500/50"
                            onClick={() => document.getElementById(`frontal-${index}`)?.click()}
                          >
                            {element.uploading ? (
                              <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
                            ) : element.frontalImageUrl ? (
                              <img src={getAssetUrl(element.frontalImageUrl)} alt="Frontal" className="w-full h-full object-cover" />
                            ) : (
                              <Plus className="w-4 h-4 text-zinc-500" />
                            )}
                          </div>
                          <input
                            id={`frontal-${index}`}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleElementFrontalUpload(index, e.target.files?.[0])}
                          />
                        </div>
                        
                        {/* Reference Images */}
                        {[...Array(3)].map((_, refIdx) => (
                          <div key={refIdx} className="space-y-1">
                            <Label className="text-[10px] text-zinc-500">
                              Ref {refIdx + 1} {refIdx === 0 ? "" : "(opt)"}
                            </Label>
                            <div 
                              className="aspect-square rounded border border-dashed border-zinc-600 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-orange-500/50 relative group"
                              onClick={() => {
                                if (!element.referenceImageUrls[refIdx]) {
                                  document.getElementById(`ref-${index}-${refIdx}`)?.click();
                                }
                              }}
                            >
                              {element.referenceImageUrls[refIdx] ? (
                                <>
                                  <img src={getAssetUrl(element.referenceImageUrls[refIdx])} alt={`Ref ${refIdx + 1}`} className="w-full h-full object-cover" />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-5 w-5 bg-red-900/80 hover:bg-red-800 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeElementRef(index, refIdx);
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </>
                              ) : (
                                <Plus className="w-4 h-4 text-zinc-600" />
                              )}
                            </div>
                            <input
                              id={`ref-${index}-${refIdx}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleElementRefUpload(index, e.target.files);
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Prompt */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-orange-400" />
                  Generation Prompt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Describe the motion... Use @Element1, @Element2 to reference your characters."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[120px] bg-zinc-800/50 border-zinc-700 text-sm"
                  disabled={generating}
                />
                {elements.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {elements.map((_, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
                        onClick={() => insertElementSyntax(index)}
                      >
                        @Element{index + 1}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Negative Prompt</Label>
                  <Input
                    placeholder="What to avoid..."
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-sm"
                    disabled={generating}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-orange-400" />
                  Generation Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400">Duration</Label>
                    <span className="text-xs font-medium text-orange-400">{duration}s</span>
                  </div>
                  <div className="flex gap-2">
                    {[5, 10, 15].map(d => (
                      <Button
                        key={d}
                        variant={duration === d ? "default" : "outline"}
                        size="sm"
                        className={duration === d ? "bg-orange-600" : "border-zinc-700"}
                        onClick={() => setDuration(d)}
                        disabled={generating}
                      >
                        {d}s
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Aspect Ratio</Label>
                    <Select value={aspectRatio} onValueChange={setAspectRatio} disabled={generating}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["16:9", "9:16", "1:1"].map(ar => (
                          <SelectItem key={ar} value={ar}>{ar}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between pt-6">
                    <Label className="text-xs text-zinc-400">Generate Audio</Label>
                    <Switch
                      checked={generateAudio}
                      onCheckedChange={setGenerateAudio}
                      disabled={generating}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim() || !startImageUrl}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-medium"
              data-testid="generate-kling3-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Generate with Kling 3.0
                </>
              )}
            </Button>

            {/* Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-orange-500/50 bg-orange-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-orange-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{Math.round(jobStatus.progress_percent)}%</span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.video_url && (
                    <div className="mt-4">
                      <video src={getAssetUrl(jobStatus.video_url)} controls className="w-full rounded-lg bg-black" />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => handleDownloadVideo(jobStatus.video_url, jobStatus.job_id)}
                      >
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History */}
          <div className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-orange-400" />
                    History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="h-6 w-6 p-0">
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">No generations yet</div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge variant="outline" className={`text-[10px] ${
                                job.status === "completed" ? "text-green-400 border-green-500/50" :
                                job.status === "failed" ? "text-red-400 border-red-500/50" :
                                "text-orange-400 border-orange-500/50"
                              }`}>
                                {job.status}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(job.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-zinc-300 line-clamp-2 mb-2">{job.prompt}</p>
                            {job.status === "completed" && job.video_url && (
                              <video src={getAssetUrl(job.video_url)} controls className="w-full rounded-lg bg-black mb-2" />
                            )}
                            <div className="flex gap-2">
                              {job.status === "completed" && job.video_url && (
                                <Button variant="ghost" size="sm" className="flex-1 text-[10px] h-7 text-green-400" onClick={() => handleDownloadVideo(job.video_url, job.job_id)}>
                                  <Download className="w-3 h-3 mr-1" /> Download
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="text-[10px] h-7 text-red-400" onClick={() => handleDeleteJob(job.job_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* Tips */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-1">
                <p>• Use <strong className="text-orange-400">@Element1</strong> syntax to reference characters</p>
                <p>• Add frontal + side/back refs for better consistency</p>
                <p>• Up to 4 elements with 5 ref images each</p>
                <p>• 5, 10, or 15 second durations</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
